import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import {
  DashboardPageSkeleton,
  DashboardSyncPageSkeleton,
  ProjectsHomePageSkeleton,
  ProtectedAppShellSkeleton,
  SettingsPageSkeleton,
} from '../components/common/LoadingSkeletons.js';
import { Button } from '../components/ui/button.js';
import { useAuth } from '../hooks/useAuth.js';
import {
  getCheckoutReturnToken,
  shouldForceProtectedBootstrapBillingRefresh,
  useSubscriptionAccess,
} from '../hooks/useSubscriptionAccess.js';
import { queryKeys } from '../lib/query/keys.js';

interface RequireAuthRouteProps {
  children: React.ReactNode;
}

const AUTH_RETURN_RECOVERY_DELAY_MS = 4_000;

function buildRecoveryStorageKey(pathname: string): string {
  return `anysync:auth-return-recovery:${pathname}`;
}

function ProtectedRouteRecoveryState({
  onRetry,
  onSignOut,
}: {
  onRetry: () => void;
  onSignOut: () => Promise<void>;
}) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-6 py-12">
      <div className="flex w-full max-w-[440px] flex-col items-center gap-4 rounded-2xl border border-border bg-card p-8 text-center shadow-[0_1px_4px_rgba(18,23,28,0.04)]">
        <h1 className="text-xl font-medium tracking-[-0.03em] text-foreground">
          Finishing your sign-in
        </h1>
        <p className="text-sm leading-6 text-muted-foreground">
          We are still confirming your access. This is taking longer than expected, but you
          should not stay stuck on this screen.
        </p>
        <div className="flex w-full flex-col gap-2 sm:flex-row">
          <Button type="button" className="flex-1" onClick={onRetry}>
            Retry now
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => {
              void onSignOut();
            }}
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

function resolveProtectedLoadingSkeleton(pathname: string): JSX.Element {
  const segments = pathname.split('/').filter(Boolean);

  if (pathname === '/projects') {
    return <ProjectsHomePageSkeleton />;
  }

  if (segments[0] === 'settings') {
    return <SettingsPageSkeleton />;
  }

  if (segments[0] === 'projects') {
    if (segments[2] === 'settings') {
      return <SettingsPageSkeleton />;
    }

    if (segments[2] === 'collections') {
      return <DashboardPageSkeleton />;
    }

    if (segments.length >= 4) {
      if (segments[3] === 'sync') {
        return <DashboardSyncPageSkeleton />;
      }

      return <DashboardPageSkeleton />;
    }
  }

  return <ProtectedAppShellSkeleton />;
}

export default function RequireAuthRoute({ children }: RequireAuthRouteProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { user, isLoading, isBypassSession, signOut } = useAuth();
  const billingSnapshot = user ? queryClient.getQueryData(queryKeys.billingSnapshot(user.id)) : null;
  const forceRefreshOnBootstrap = shouldForceProtectedBootstrapBillingRefresh({
    userId: user?.id ?? null,
    isBypassSession,
    checkoutReturnToken:
      typeof window === 'undefined' ? null : getCheckoutReturnToken(window.location.search),
    hasCachedBillingSnapshot: billingSnapshot !== undefined,
  });
  const {
    isLoading: isSubscriptionLoading,
    hasResolved,
    error: subscriptionError,
  } = useSubscriptionAccess({
    user,
    isBypassSession,
    forceRefresh: forceRefreshOnBootstrap,
  });
  const [showRecoveryState, setShowRecoveryState] = React.useState(false);

  const hasProtectedBootstrapPending =
    isLoading || (Boolean(user) && !isBypassSession && (isSubscriptionLoading || !hasResolved));

  React.useEffect(() => {
    if (typeof window === 'undefined' || !user || isBypassSession) {
      return;
    }

    const storageKey = buildRecoveryStorageKey(`${user.id}:${location.pathname}`);

    if (!hasProtectedBootstrapPending) {
      window.sessionStorage.removeItem(storageKey);
      setShowRecoveryState(false);
      return;
    }

    const timer = window.setTimeout(() => {
      const alreadyReloaded = window.sessionStorage.getItem(storageKey) === '1';
      if (!alreadyReloaded && forceRefreshOnBootstrap) {
        window.sessionStorage.setItem(storageKey, '1');
        window.location.reload();
        return;
      }

      setShowRecoveryState(true);
    }, AUTH_RETURN_RECOVERY_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    forceRefreshOnBootstrap,
    hasProtectedBootstrapPending,
    isBypassSession,
    location.pathname,
    user,
  ]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !user) {
      return;
    }

    if (!hasProtectedBootstrapPending) {
      window.sessionStorage.removeItem(buildRecoveryStorageKey(`${user.id}:${location.pathname}`));
      setShowRecoveryState(false);
    }
  }, [hasProtectedBootstrapPending, location.pathname, user]);

  if (isLoading) {
    if (showRecoveryState) {
      return (
        <ProtectedRouteRecoveryState
          onRetry={() => {
            window.location.reload();
          }}
          onSignOut={async () => {
            await signOut();
            window.location.assign('/login');
          }}
        />
      );
    }
    return resolveProtectedLoadingSkeleton(location.pathname);
  }

  if (!user) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  if (!isBypassSession && isSubscriptionLoading) {
    if (showRecoveryState) {
      return (
        <ProtectedRouteRecoveryState
          onRetry={() => {
            window.location.reload();
          }}
          onSignOut={async () => {
            await signOut();
            window.location.assign('/login');
          }}
        />
      );
    }
    return resolveProtectedLoadingSkeleton(location.pathname);
  }

  if (!isBypassSession && !hasResolved) {
    if (showRecoveryState || subscriptionError) {
      return (
        <ProtectedRouteRecoveryState
          onRetry={() => {
            window.location.reload();
          }}
          onSignOut={async () => {
            await signOut();
            window.location.assign('/login');
          }}
        />
      );
    }
    return resolveProtectedLoadingSkeleton(location.pathname);
  }

  return <>{children}</>;
}
